import { BadgeCardRow } from '@bp/ui-components';
import dayjs from 'dayjs';
import { CardType } from '../components/TimetableVersion/graphql/types';
import { TimetableVersionDataQuery } from '../types/planung-graphql-client-defs';
import { createIntArray } from './arrayFunc';

type TimetableVersionView = TimetableVersionDataQuery['timetableVersionData']['view'];
type TimetableVersionEditor = TimetableVersionDataQuery['timetableVersionData']['editor'];

export function buildCardsFromTimetableVersionData(
  view: TimetableVersionView,
  editor: TimetableVersionEditor,
): CardType[] {
  const tgeMap = new Map(view.timeGridEntries.map((e) => [e.uuid, e]));
  const teacherMap = new Map(view.teachers.map((t) => [t.uuid, t]));
  const classMap = new Map(view.classes.map((c) => [c.uuid, c]));
  const groupMap = new Map(view.groups.map((g) => [g.uuid, g]));
  const roomMap = new Map(view.rooms.map((r) => [r.uuid, r]));
  const subjectMap = new Map(view.subjects.map((s) => [s.uuid, s]));
  const scMap = new Map(view.subjectContainers.map((sc) => [sc.uuid, sc]));
  const lessonMap = new Map(editor?.lessons.map((l) => [l.uuid, l]) ?? []);

  return view.cards.map((gridCard): CardType => {
    const startTge = gridCard.startTimeGridEntryUuid ? tgeMap.get(gridCard.startTimeGridEntryUuid) : undefined;
    const endTge = gridCard.endTimeGridEntryUuid ? tgeMap.get(gridCard.endTimeGridEntryUuid) : undefined;
    const startTime = startTge ? dayjs(startTge.start).format('HH:mm') : null;
    const endTime = endTge ? dayjs(endTge.end).format('HH:mm') : null;

    // Fachräume (subject.defaultRooms) für den Board-Raumvorrat aufbauen. Nur echte Subjects
    // tragen __typename 'SubjectItem' + defaultRooms – setDefaultCardRoomsByGroup wertet
    // ausschließlich diesen Zweig aus (SubjectContainer haben keine Fachräume).
    const subjectContainer = gridCard.isSubjectContainer ? scMap.get(gridCard.subjectUuid) : undefined;
    const subjectView = gridCard.isSubjectContainer ? undefined : subjectMap.get(gridCard.subjectUuid);
    // Die timetableVersionData-Sicht liefert die Fachfarbe als direktes `color`-Feld, der CardType
    // (und die Fachfarben-Ansicht der GridCard) erwarten sie aber unter `timetableConfig.color`.
    // Ohne dieses Nachbauen ist `timetableConfig` zur Laufzeit undefined und alle Karten sind im
    // Fachfarben-Modus grau.
    const subject =
      (subjectContainer
        ? {
            ...subjectContainer,
            timetableConfig: subjectContainer.color ? { color: subjectContainer.color } : null,
          }
        : undefined) ??
      (subjectView
        ? {
            ...subjectView,
            __typename: 'SubjectItem' as const,
            timetableConfig: subjectView.color ? { color: subjectView.color } : null,
            defaultRooms: subjectView.defaultRoomUuids.map((uuid) => ({ __typename: 'RoomRefItem' as const, uuid })),
          }
        : undefined);

    // SubjectContainer-Karten: Teacher kommen aus den TeachingBlock-Slots
    const teacherUuids = gridCard.isSubjectContainer
      ? [...new Set(gridCard.subjectContainerSlots.flatMap((slot) => slot.teacherUuids))]
      : gridCard.teacherUuids;
    const teachers = teacherUuids.flatMap((uuid) => {
      const t = teacherMap.get(uuid);
      // Lehrerraum (defaultRoom) für den Board-Raumvorrat mitgeben
      return t
        ? [{ ...t, defaultRoom: t.defaultRoomUuid ? { __typename: 'Room' as const, uuid: t.defaultRoomUuid } : null }]
        : [];
    });

    const classes = gridCard.classUuids.flatMap((uuid) => {
      const c = classMap.get(uuid);
      // Klassenraum (defaultRoom) für den Board-Raumvorrat mitgeben
      return c
        ? [{ ...c, defaultRoom: c.defaultRoomUuid ? { __typename: 'Room' as const, uuid: c.defaultRoomUuid } : null }]
        : [];
    });

    const groups = gridCard.groupUuids.flatMap((uuid) => {
      const g = groupMap.get(uuid);
      return g ? [g] : [];
    });

    // SubjectContainer-Karten: Räume aus den TeachingBlock-Slots (analog teachers)
    const roomUuids = gridCard.isSubjectContainer
      ? [...new Set(gridCard.subjectContainerSlots.flatMap((slot) => slot.roomUuids))]
      : gridCard.roomUuids;
    const rooms = roomUuids.flatMap((uuid) => {
      const r = roomMap.get(uuid);
      return r ? [{ uuid: r.uuid, name: r.name }] : [];
    });

    const lessonClasses = gridCard.lessonClasses.map((lc) => ({
      __typename: 'LessonClass' as const,
      uuid: lc.uuid,
      class: { __typename: 'Class' as const, uuid: lc.classUuid },
      groups: lc.groupUuids.map((uuid) => ({ __typename: 'Group' as const, uuid })),
      usedDivision: lc.usedDivisionUuid ? { __typename: 'Division' as const, uuid: lc.usedDivisionUuid } : null,
    }));

    // Fallback mit uuid auch ohne editor-Daten (readonly): getCardsStack gruppiert
    // über lesson.uuid – ohne uuid verschmelzen freie Karten verschiedener Lessons.
    // Der Lesson-Raumvorrat kommt jetzt direkt von der Card (lessonRoomSupplyUuids) und ist
    // damit auch ohne Editor-Daten verfügbar.
    const roomSupplyConnection = {
      __typename: 'LessonRoomSupplyConnection' as const,
      edges: gridCard.lessonRoomSupplyUuids.map((uuid) => ({
        __typename: 'LessonRoomSupplyRelationship' as const,
        node: { __typename: 'Room' as const, uuid },
      })),
    };
    const gridLesson = lessonMap.get(gridCard.lessonUuid);
    const lesson = gridLesson
      ? {
          ...gridLesson,
          __typename: 'Lesson' as const,
          roomSupplyConnection,
        }
      : {
          __typename: 'Lesson' as const,
          uuid: gridCard.lessonUuid,
          roomSupplyConnection,
        };

    // Badge rows from classGridCards
    const classGridCards = gridCard.classGridCards;
    const positions: number[] = classGridCards.map((c) => ((c.divisionsCount ?? 1) > 1 ? (c.usedPosition ?? 1) : 0));
    const maxDivisions = Math.max(...classGridCards.map((c) => c.divisionsCount ?? 1), 1);
    const cardWithBiggestDivisionsCount = classGridCards.find((c) => (c.divisionsCount ?? 1) === maxDivisions);
    const badgeCardRows: BadgeCardRow[] = createIntArray(maxDivisions).map((i) =>
      positions.includes(i)
        ? { rowColor: '', colColors: cardWithBiggestDivisionsCount?.colors ?? [] }
        : { rowColor: '', colColors: [] },
    );

    return {
      __typename: 'TimetableCard' as const,
      uuid: gridCard.uuid,
      duration: gridCard.duration,
      weekday: gridCard.weekday,
      locked: gridCard.locked,
      startTimeGridEntry: gridCard.startTimeGridEntryUuid ? { uuid: gridCard.startTimeGridEntryUuid } : null,
      endTimeGridEntry: gridCard.endTimeGridEntryUuid ? { uuid: gridCard.endTimeGridEntryUuid } : null,
      start: gridCard.startTimeGridEntryUuid ?? null,
      end: gridCard.endTimeGridEntryUuid ?? null,
      startTime,
      endTime,
      subject: subject as CardType['subject'],
      isSubjectContainer: gridCard.isSubjectContainer,
      lessonIsElective: gridCard.lessonIsElective,
      someLessonClassIsFunctionalClass: gridCard.someLessonClassIsFunctionalClass,
      lesson: lesson as unknown as CardType['lesson'],
      lessonClasses: lessonClasses as CardType['lessonClasses'],
      classGridCards,
      teachers: teachers as unknown as CardType['teachers'],
      classes: classes as unknown as CardType['classes'],
      groups: groups as unknown as CardType['groups'],
      rooms,
      subjectContainerSlots: gridCard.isSubjectContainer
        ? gridCard.subjectContainerSlots.map((slot) => ({
            teacherUuids: slot.teacherUuids,
            roomUuids: slot.roomUuids,
            startDate: slot.startDate ?? null,
            endDate: slot.endDate ?? null,
          }))
        : undefined,
      badgeCardRows,
      badgeCardTextColor: cardWithBiggestDivisionsCount?.textColor ?? '',
    };
  });
}
